/**
 * DemoExperience — 3-column comparison experience.
 *
 * Клиент (trigger) → Shadow Draft (AI) → Оператор (comparison).
 * Используется на лендинге (DemoSection) и отдельной странице (DemoPage).
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  useCreateDemoSession,
  useSendDemoMessage,
  useSendOperatorReply,
  useDemoSession,
  type DemoMessage,
  type DemoProposal,
  type OperatorReplyResult,
} from '../../api/demo';
import { ClientColumn } from './ClientColumn';
import { ShadowColumn } from './ShadowColumn';
import { OperatorColumn } from './OperatorColumn';

// ============================================================================
// Types
// ============================================================================

/** Один раунд: вопрос клиента → draft AI → ответ оператора → comparison */
export interface DemoRound {
  clientMessage: DemoMessage;
  proposal: DemoProposal | null;
  operatorReply: string | null;
  comparisonResult: OperatorReplyResult | null;
}

type ActiveTab = 'client' | 'shadow' | 'operator';

// ============================================================================
// DemoExperience
// ============================================================================

export function DemoExperience(): JSX.Element {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(3);
  const [clientInput, setClientInput] = useState('');
  const [operatorInput, setOperatorInput] = useState('');
  const [comparisonResults, setComparisonResults] = useState<Record<string, OperatorReplyResult>>({});
  const [operatorReplies, setOperatorReplies] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<ActiveTab>('client');

  const createSession = useCreateDemoSession();
  const sendMessage = useSendDemoMessage(sessionId);
  const sendOperatorReply = useSendOperatorReply(sessionId);
  const { data: sessionData } = useDemoSession(sessionId);

  const messages = sessionData?.messages ?? [];
  const proposals = sessionData?.proposals ?? [];
  const clientMessages = messages.filter(m => m.actorRole === 'client');

  // Собираем раунды (мемоизация — polling каждые 2 сек)
  const rounds: DemoRound[] = useMemo(() =>
    clientMessages.map(msg => {
      const proposal = msg.turnId
        ? proposals.find(p => p.sourceTurnId === msg.turnId) ?? null
        : null;
      return {
        clientMessage: msg,
        proposal,
        operatorReply: operatorReplies[msg.id] ?? null,
        comparisonResult: comparisonResults[msg.id] ?? null,
      };
    }),
    [clientMessages.length, proposals.length, operatorReplies, comparisonResults],
  );

  // Текущий раунд — последний без ответа оператора
  const currentRound = rounds.length > 0 ? (rounds[rounds.length - 1] ?? null) : null;
  const hasPendingDraft = currentRound?.proposal === null && currentRound !== null;
  const hasDraft = currentRound?.proposal !== null && currentRound?.proposal?.status !== 'thinking';
  const awaitingOperator = hasDraft && currentRound?.operatorReply === null;

  // Инициализация сессии
  const sessionInitRef = useRef(false);
  const createSessionRef = useRef(createSession);
  createSessionRef.current = createSession;
  useEffect(() => {
    if (!sessionId && !sessionInitRef.current) {
      sessionInitRef.current = true;
      createSessionRef.current.mutate(undefined, {
        onSuccess: (result) => {
          setSessionId(result.sessionId);
          setRemaining(result.maxMessages);
        },
      });
    }
  }, [sessionId]);

  // Автопереключение табов (mobile)
  const prevProposalsLen = useRef(proposals.length);
  const tabTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (proposals.length > prevProposalsLen.current) {
      const lastProposal = proposals[proposals.length - 1];
      if (lastProposal && lastProposal.status !== 'thinking') {
        setActiveTab('shadow');
        clearTimeout(tabTimerRef.current);
        tabTimerRef.current = setTimeout(() => setActiveTab('operator'), 1500);
      }
    }
    prevProposalsLen.current = proposals.length;
  }, [proposals.length]);

  useEffect(() => {
    return () => clearTimeout(tabTimerRef.current);
  }, []);

  // Отправка сообщения клиента
  const handleSendClient = useCallback(() => {
    const text = clientInput.trim();
    if (!text || sendMessage.isPending || remaining <= 0) return;
    setClientInput('');
    sendMessage.mutate(text, {
      onSuccess: (result) => {
        setRemaining(result.remaining);
        setActiveTab('shadow');
      },
    });
  }, [clientInput, sendMessage, remaining]);

  // Отправка ответа оператора
  const handleSendOperator = useCallback(() => {
    const text = operatorInput.trim();
    if (!text || sendOperatorReply.isPending || !currentRound) return;
    const msgId = currentRound.clientMessage.id;
    setOperatorInput('');
    setOperatorReplies(prev => ({ ...prev, [msgId]: text }));
    sendOperatorReply.mutate(text, {
      onSuccess: (result) => {
        setComparisonResults(prev => ({ ...prev, [msgId]: result }));
      },
    });
  }, [operatorInput, sendOperatorReply, currentRound]);

  // Клик по chip
  const handleChipClick = useCallback((text: string) => {
    if (remaining <= 0 || sendMessage.isPending) return;
    sendMessage.mutate(text, {
      onSuccess: (result) => {
        setRemaining(result.remaining);
        setActiveTab('shadow');
      },
    });
  }, [sendMessage, remaining]);

  const limitReached = remaining <= 0 && (!currentRound || currentRound.comparisonResult !== null);

  return (
    <div className="w-full">
      {/* Mobile tabs */}
      <div className="flex md:hidden border-b border-border-subtle mb-4">
        {(['client', 'shadow', 'operator'] as const).map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
              activeTab === tab
                ? 'text-accent-brand border-b-2 border-accent-brand'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            {tab === 'client' ? 'Клиент' : tab === 'shadow' ? 'Shadow' : 'Оператор'}
            {tab === 'shadow' && hasPendingDraft && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-accent-ai rounded-full animate-pulse" />
            )}
            {tab === 'operator' && awaitingOperator && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-accent-success rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* 3-column layout (desktop) / single column (mobile with tabs) */}
      <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-border-subtle rounded-panel border border-border-subtle bg-white shadow-medium overflow-hidden min-h-[400px]">
        <div className={`${activeTab !== 'client' ? 'hidden md:flex' : 'flex'} flex-col`}>
          <ClientColumn
            rounds={rounds}
            inputValue={clientInput}
            onInputChange={setClientInput}
            onSend={handleSendClient}
            onChipClick={handleChipClick}
            disabled={remaining <= 0 || sendMessage.isPending}
            isPending={sendMessage.isPending}
            limitReached={limitReached}
          />
        </div>

        <div className={`${activeTab !== 'shadow' ? 'hidden md:flex' : 'flex'} flex-col`}>
          <ShadowColumn rounds={rounds} />
        </div>

        <div className={`${activeTab !== 'operator' ? 'hidden md:flex' : 'flex'} flex-col`}>
          <OperatorColumn
            rounds={rounds}
            currentRound={currentRound}
            inputValue={operatorInput}
            onInputChange={setOperatorInput}
            onSend={handleSendOperator}
            disabled={!awaitingOperator || sendOperatorReply.isPending}
            isPending={sendOperatorReply.isPending}
            limitReached={limitReached}
          />
        </div>
      </div>
    </div>
  );
}
